import cn from 'classnames';
import { isFeatureHighlighted } from '@/entities/plan';
import { Separator, Text, Button as BaseButton, FieldRequiredMark } from '@/shared/ui/core';
import { CheckIcon, CaretDownIcon } from '@phosphor-icons/react/ssr';
import { PlanCardProps } from '../model/plan-card.props';
import styles from './plan-card.module.scss';


export const PlanCard = ({ plan, billingPeriod, isSelected, isStandalone, Button, openId, setOpenId, teamEnabled }: PlanCardProps) => {
    const isMonthly = billingPeriod === 'monthly';
    const price     = isMonthly ? plan.monthlyPrice : plan.yearlyPrice;
    const isOpen = openId === plan.id;

    return (
        <div
            className={ cn(styles.root, {
                [styles.popular]   : plan.isPopular && !isStandalone,
                [styles.selected]  : isSelected,
                [styles.standalone]: isStandalone,
            }) }
        >
            {
                plan.isPopular &&
                !isStandalone &&
                <Text
                    size="sm"
                    weight="medium"
                    className={ styles.popular_badge }
                >
                    Most popular
                </Text>
            }
            {
                !plan.isPopular &&
                !isStandalone &&
                <div className={ styles.placeholder }/>
            }


            <div className={ styles.inner }>
                <div className={ styles.body }>
                    <Text
                        as="h3"
                        variant="heading"
                        weight="semibold"
                        lineHeight={ 1 }
                        className={ styles.title }
                    >
                        { plan.name }

                        <BaseButton
                            variant='naked'
                            className={ cn(styles.toggle, { [styles.open]: isOpen }) }
                            onClick={ () => setOpenId?.(isOpen ? '' : plan.id) }
                        >
                            <CaretDownIcon size={ 20 }/>
                        </BaseButton>
                    </Text>

                    {
                        price === null &&
                        <div className={ styles.price }>
                            <Text
                                as="span"
                                size="xxl"
                                weight="medium"
                                className={ styles.amount }
                            >
                                Custom
                            </Text>
                        </div>
                    }
                    {
                        price !== null &&
                        <div className={ styles.price }>
                            <Text
                                as="span"
                                size="xxl"
                                weight="medium"
                                className={ styles.amount }
                            >
                                €{ price }
                                {
                                    !!price &&
                                    <FieldRequiredMark/>
                                }
                            </Text>

                            <Text
                                as="span"
                                size="sm"
                                tone="secondary"
                                className={ styles.period }
                            >
                                /month
                            </Text>
                        </div>
                    }
                    {
                        plan.isEnterprise &&
                        <Text
                            as="span"
                            size="sm"
                            tone="secondary"
                            lineHeight={ 1 }
                        >
                            {
                                isMonthly
                                    ? 'Custom billed monthly'
                                    : 'Custom billed yearly'
                            }
                        </Text>
                    }
                    {
                        !plan.isEnterprise &&
                        <div className={ styles.billing }>
                            {
                                isMonthly &&
                                <Text
                                    as="span"
                                    size="sm"
                                    tone="secondary"
                                    lineHeight={ 1 }
                                >
                                    billed monthly
                                </Text>
                            }
                            {
                                !isMonthly &&
                                <Text
                                    as="span"
                                    size="sm"
                                    tone="primary"
                                    lineHeight={ 1 }
                                >
                                    {
                                        !!plan.originalYearPrice &&
                                        <Text
                                            as="span"
                                            size="sm"
                                            tone="destructive"
                                            lineHeight={ 1 }
                                            className={ styles.original_price }
                                        >
                                            €{ plan.originalYearPrice }
                                        </Text>
                                    }
                                    { ' ' }
                                    {
                                        !!plan.yearPrice &&
                                        <Text
                                            as="span"
                                            size="sm"
                                            tone="primary"
                                            lineHeight={ 1 }
                                        >
                                            €{ plan.yearPrice }
                                        </Text>
                                    }
                                    { ' ' }
                                    <Text
                                        as="span"
                                        size="sm"
                                        tone="secondary"
                                        lineHeight={ 1 }
                                    >
                                        billed yearly
                                    </Text>
                                </Text>
                            }
                        </div>
                    }

                    <div className={ cn({ [styles.hidden]: !isOpen })}>

                        <Separator className={ styles.separator }/>

                        <ul className={ styles.features }>
                            {
                                plan.features.map((feature, index) => (
                                    <li
                                        key={ feature.id }
                                        className={ cn(styles.feature, {[styles.disabled]: !isFeatureHighlighted(feature, teamEnabled) }) }
                                    >
                                        <CheckIcon
                                            size={ 16 }
                                            className={ styles.check }
                                            aria-hidden
                                        />

                                        <Text
                                            size="md"
                                            weight="medium"
                                            tone='inherit'
                                            className={ styles.description }
                                        >
                                            { feature.description }
                                            {
                                                !index &&
                                                <>
                                                    <FieldRequiredMark className={ styles.red_mark }/>
                                                    <FieldRequiredMark className={ styles.red_mark }/>
                                                </>
                                            }
                                        </Text>
                                    </li>
                                ))
                            }
                        </ul>

                    </div>
                </div>

                { Button }
            </div>
        </div>
    );
};
